'use client';
import React, { useRef } from 'react';
import useOfferComparisonStore from '../store/use-offer-comparison-store';
import { Button, Spinner } from '@heroui/react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import InvoiceUpload, {
  InvoiceUploadRef,
} from '@/app/components/invoice-upload';
import { ReferenceTariffType } from '@/app/types/reference-tariff-type';
import useInvoiceUploadStore from '@/lib/invoice-upload/store/use-invoice-upload-store';
import { useCustomizationContext } from '../customizations-provider';

export default function InvoiceUploadCard() {
  const t = useTranslations('invoiceUpload');
  const offer = useOfferComparisonStore((state) => state.offer);
  const comparisonMode = useOfferComparisonStore((state) => state.mode);
  const isInvoiceLoading = useInvoiceUploadStore((state) => state.isLoading);
  const isOfferLoading = useOfferComparisonStore((state) => state.isLoading);
  const isLoading = isInvoiceLoading || isOfferLoading;
  const invoiceUploadRef = useRef<InvoiceUploadRef>(null);

  const title =
    comparisonMode === ReferenceTariffType.average
      ? 'upload.title'
      : 'repeatUpload.title';
  const body =
    comparisonMode === ReferenceTariffType.average
      ? 'upload.body'
      : 'repeatUpload.body';

  if (offer == null) {
    return;
  }
  return (
    <div className="outline outline-1 outline-[var(--color-border-default)] p-6 rounded-3.75 bg-[var(--color-card-primary)]">
      <p
        data-testid="invoice-upload-card-title"
        className="font-semibold text-xl leading-[34px]"
      >
        {t(title)}
      </p>
      <p
        data-testid="invoice-upload-card-body"
        className="pt-3 md:pt-4 pb-6 md:pb-8 text-lg"
      >
        {t(body)}
      </p>
      <Button
        data-testid="invoice-upload-card-button"
        isDisabled={isLoading}
        className="btn-secondary w-full min-h-[50px] text-sm xs:text-base"
        endContent={!isLoading && <UploadIcon />}
        onPress={() => invoiceUploadRef.current?.open()}
      >
        {isLoading ? (
          <Spinner data-testid="invoice-upload-card-spinner" />
        ) : (
          <InvoiceUpload ref={invoiceUploadRef}>
            {t('upload.cta')}
          </InvoiceUpload>
        )}
      </Button>
    </div>
  );
}

const UploadIcon = () => {
  const c = useCustomizationContext();
  const uploadImage = c('invoiceUpload.image');
  return (
    <Image
      data-testid="upload-icon"
      alt={uploadImage.description}
      src={uploadImage.url}
      width={uploadImage.width}
      height={uploadImage.height}
    />
  );
};
